import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { LogOut, ArrowLeft, LogIn, Users, BadgeCheck, CalendarDays, IndianRupee, ShieldCheck, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { api, googleLogin, logout } from "@/lib/api";
import { Logo } from "@/components/Logo";

const WORKSPACE = "@keyscapeadvisorysvs.com";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-white/10 bg-white/[0.02] p-6" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">{label}</p>
      <Icon size={16} className="text-[#C8A97E]" />
    </div>
    <p className="font-display text-3xl md:text-4xl text-[#F8F9FA] mt-4">{value}</p>
  </div>
);

export default function EmployeeLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [state, setState] = useState(location.state?.user ? "ready" : "checking");
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!res.data.is_admin) {
          setState("denied");
        } else {
          setUser(res.data);
          setState("ready");
        }
      } catch {
        setState("denied");
      }
    })();
  }, [location.state]);

  useEffect(() => {
    if (state !== "ready" || !user?.is_admin) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [state, user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const exportLeads = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "keyscape-leads.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("All leads exported to Excel");
    } catch { toast.error("Export failed"); }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center" data-testid="employee-loading">
        <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#8A8D98] animate-pulse">Verifying access…</p>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex flex-col items-center justify-center gap-8" data-testid="employee-access-denied">
        <p className="font-display text-3xl text-[#F8F9FA]">Employee access only.</p>
        <p className="text-sm text-[#8A8D98] text-center max-w-sm">
          Sign in with your Google Workspace account — only <span className="text-[#C8A97E]">{WORKSPACE}</span> identities are granted rights here.
        </p>
        <button onClick={() => googleLogin("/employee-login")} data-testid="employee-login-button" className="btn-gold flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em] font-medium">
          <span>Continue with Google</span>
          <LogIn size={15} className="relative z-10" />
        </button>
        <Link to="/" className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-[#C8A97E] transition-colors" data-testid="employee-back-link">
          Back to site
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10] px-6 md:px-10 py-8" data-testid="employee-login-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between pb-6 border-b border-white/10">
          <div className="flex items-center gap-6">
            <Logo testId="employee-logo" />
            <Link to="/land-acquisition" data-testid="employee-pipeline-link" className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-[#C8A97E] transition-colors">
              <ArrowLeft size={14} /> Pipeline
            </Link>
            <div>
              <p className="font-mono-lux text-[9px] uppercase tracking-[0.3em] text-[#C8A97E]">Google OAuth Workspace · {WORKSPACE} only</p>
              <h1 className="font-display text-2xl md:text-3xl text-[#F8F9FA]">Employee Login</h1>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <button onClick={exportLeads} data-testid="employee-export-leads" className="flex items-center gap-2 border border-white/15 hover:border-[#C8A97E] px-4 py-2.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] text-[#8A8D98] hover:text-[#C8A97E] transition-colors">
              <Download size={13} /> Export Leads
            </button>
            <span className="hidden sm:block font-mono-lux text-[10px] uppercase tracking-[0.2em] text-[#8A8D98]" data-testid="employee-user-email">{user?.email}</span>
            <button onClick={handleLogout} data-testid="employee-logout-button" className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-red-400 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 py-8">
          <StatCard icon={Users} label="Total Leads" value={stats?.total_leads ?? "—"} testId="stat-total-leads" />
          <StatCard icon={BadgeCheck} label="Auto-Qualified" value={stats?.qualified_leads ?? "—"} testId="stat-qualified-leads" />
          <StatCard icon={CalendarDays} label="Consultations" value={stats?.total_consultations ?? "—"} testId="stat-consultations" />
          <StatCard icon={IndianRupee} label="Pipeline Value (indicative)" value={stats ? `₹${stats.pipeline_value_cr.toFixed(2)} Cr` : "—"} testId="stat-pipeline-value" />
          <StatCard icon={ShieldCheck} label="Workspace Sign-ins" value={stats?.google_signins ?? "—"} testId="stat-signins" />
        </div>

        <div className="border border-white/10 bg-white/[0.02] p-6 mb-10 overflow-x-auto" data-testid="employees-panel">
          <p className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] mb-6 flex items-center gap-2">
            <Clock size={13} className="text-[#C8A97E]" /> Workspace Sign-ins — Last Logged In
          </p>
          <table className="w-full text-sm min-w-[560px]" data-testid="employees-table">
            <thead>
              <tr className="font-mono-lux text-[10px] uppercase tracking-[0.2em] text-[#8A8D98] text-left">
                <th className="pb-4 font-medium">Employee</th>
                <th className="pb-4 font-medium">Workspace ID</th>
                <th className="pb-4 font-medium">Role</th>
                <th className="pb-4 font-medium">Last Logged In</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-[#8A8D98]" data-testid="employees-empty">No workspace sign-ins yet.</td></tr>
              )}
              {employees.map((e) => (
                <tr key={e.user_id} className="border-t border-white/5" data-testid={`employee-row-${e.user_id}`}>
                  <td className="py-4 pr-4">
                    <div className="flex items-center gap-3">
                      {e.picture ? (
                        <img src={e.picture} alt={e.name} className="w-8 h-8 rounded-full border border-[#C8A97E]/40" />
                      ) : (
                        <div className="w-8 h-8 rounded-full border border-[#C8A97E]/40 flex items-center justify-center font-display text-sm text-[#C8A97E]">
                          {e.name?.[0] || "?"}
                        </div>
                      )}
                      <span className="text-[#F8F9FA]">{e.name}</span>
                    </div>
                  </td>
                  <td className="py-4 pr-4 font-mono-lux text-[11px] text-[#B9BCC4]">{e.email}</td>
                  <td className="py-4 pr-4">
                    <span className={`font-mono-lux text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 border ${e.is_admin ? "border-[#C8A97E]/50 text-[#C8A97E]" : "border-white/15 text-[#8A8D98]"}`}>
                      {e.is_admin ? "Admin" : "Client"}
                    </span>
                  </td>
                  <td className="py-4 font-mono-lux text-[11px] text-[#8A8D98]">
                    {e.last_login ? new Date(e.last_login).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
